"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.IkeCollectionUpgrade = void 0;
const tslib_1 = require("tslib");
const core_api_1 = require("@ikegps/core-api");
const __1 = require("..");
const __2 = require("../..");
const collection_upgrade_action_1 = require("./collection.upgrade.action");
class IkeCollectionUpgrade {
    constructor(collections, targetForm) {
        this.validations = {};
        this.actions = {};
        this.collections = collections;
        this.targetForm = targetForm;
    }
    /** Add a migration action for a field, eg drop, replace */
    addAction(action) {
        if (this.actions[action.fieldId]) {
            throw new Error('Duplicate action fieldId: ' + action.fieldId);
        }
        this.actions[action.fieldId] = collection_upgrade_action_1.IkeCollectionUpgradeActionFactory.createAction(action);
        return this;
    }
    process(log) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const output = [];
            for (const oldCollection of this.collections) {
                const newCollection = __2.IkeModel.Collection.read(yield __2.IkeModel.Collection.write(oldCollection), this.targetForm, oldCollection.loader);
                yield newCollection.ready();
                yield this.processSubForm(oldCollection, newCollection, log.child({ collectionId: oldCollection.id }));
                output.push(newCollection);
            }
            const validations = Object.keys(this.validations);
            if (validations.length > 0) {
                return { result: 'failure', fields: validations.map(c => this.validations[c]) };
            }
            return { result: 'success', collections: output };
        });
    }
    findMatchingField(field, oldSubForm, newSubForm) {
        // If the action maps the field then use that
        const action = this.actions[field.id];
        if (action != null) {
            return action.targetFieldId;
        }
        // Attempt to look for a matching field by display name
        const sourceMatches = oldSubForm.fields.filter(f => f.displayName === field.displayName && f.type === field.type);
        if (sourceMatches.length > 1) {
            // Multiple source fields, skip
            return null;
        }
        const matches = newSubForm.fields.filter(f => f.displayName === field.displayName && f.type === field.type);
        if (matches.length !== 1) {
            // Multiple target fields, skip
            return null;
        }
        return matches[0].id;
    }
    getAction(oldField) {
        return this.actions[oldField.schema.id];
    }
    processSubForm(oldSubForm, newSubForm, log) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            let failures = [];
            for (const schemaField of oldSubForm.schema.fields) {
                const oldField = oldSubForm.getField(schemaField.id);
                if (!oldField.hasValue()) {
                    continue;
                }
                // handle undefined yes/no field
                if (oldField.schema.type === core_api_1.IkeFieldType.YES_NO && oldField.displayValue === 'undefined') {
                    continue;
                }
                let targetFieldId = schemaField.id;
                const action = this.getAction(oldField);
                // Handle 'drop' as a special case when target form does not have field defined
                if (action != null && !action.requiresTargetField) {
                    if (newSubForm.hasField(targetFieldId)) {
                        const newDropField = newSubForm.getField(targetFieldId);
                        const isActionProcessed = action.process(oldField, newDropField, log);
                        if (isActionProcessed) {
                            log.trace({ oldFieldId: oldField.id, newFieldId: newDropField.id, action: action.reason }, 'Applied action');
                        }
                    }
                    continue;
                }
                // Attempt to find the mapping for a missed field
                if (!newSubForm.schema.hasField(schemaField.id)) {
                    targetFieldId = this.findMatchingField(schemaField, oldSubForm.schema, newSubForm.schema);
                    if (targetFieldId == null) {
                        log.warn({ fieldId: schemaField.id }, 'Unable to find field mapping');
                        this.validations[schemaField.id] = { reason: 'missing', fieldId: schemaField.id, fieldName: schemaField.displayName };
                        continue;
                    }
                    if (oldField.schema.type !== core_api_1.IkeFieldType.FORM) {
                        // Form duplication will handle recursively to apply rules at each level
                        const targetField = newSubForm.getField(targetFieldId);
                        targetField.Mutator.copy(oldField);
                    }
                }
                const newField = newSubForm.getField(targetFieldId);
                // field types have changed... TODO handle this
                if (newField.schema.type !== oldField.schema.type) {
                    log.error({ oldFieldId: schemaField.id, newFieldId: newField.id, oldType: oldField.schema.type, newType: newField.schema.type }, 'Mapping invalid types');
                    throw new Error('Cannot map between types');
                }
                // Duplicate a sub form
                if (__1.CollectionModelFieldSubForm.isSubFormField(oldField) && __1.CollectionModelFieldSubForm.isSubFormField(newField)) {
                    for (const oldInstance of oldField.value) {
                        const newInstance = this.getOrCreateSubformInstance(newField, oldInstance.instanceId);
                        const res = yield this.processSubForm(oldInstance, newInstance, log);
                        if (res.length > 0) {
                            failures = failures.concat(res);
                        }
                    }
                }
                if (action != null && action.process != null) {
                    const isActionProcessed = action.process(oldField, newField, log);
                    if (isActionProcessed) {
                        log.trace({ oldFieldId: oldField.id, newFieldId: newField.id, action: action.reason }, 'Applied action');
                        continue;
                    }
                }
                if (newField.displayValue !== oldField.displayValue) {
                    // Exception for IPF form upgrades: only if oldFormVersion < 3 and newFormVersion >= 3
                    // and the field is serviceCommunicationsCable or servicePowerConductor
                    const oldFormVersion = (_d = (_c = (_b = (_a = oldSubForm.schema.getForm().metadata).getIkePoleForeman) === null || _b === void 0 ? void 0 : _b.call(_a)) === null || _c === void 0 ? void 0 : _c.formVersion) !== null && _d !== void 0 ? _d : 1;
                    const newFormVersion = (_h = (_g = (_f = (_e = this.targetForm) === null || _e === void 0 ? void 0 : _e.metadata) === null || _f === void 0 ? void 0 : _f.getIkePoleForeman) === null || _g === void 0 ? void 0 : _g.call(_f)) === null || _h === void 0 ? void 0 : _h.formVersion;
                    const isIPFUpgrade = ((oldField.schema.id === 'ipf_serviceCommunicationsCable' || oldField.schema.id === 'ipf_servicePowerConductor') &&
                        newFormVersion !== undefined &&
                        oldFormVersion < 3 && newFormVersion >= 3);
                    if (isIPFUpgrade) {
                        newField.Mutator.truncatedCopy(oldField);
                    }
                    else {
                        log.error({ oldFieldId: schemaField.id, oldDisplay: oldField.displayValue, newDisplay: newField.displayValue }, 'Display miss match');
                        this.addReplaceValue(schemaField, oldField.displayValue);
                    }
                }
            }
            return failures;
        });
    }
    getOrCreateSubformInstance(subform, instanceId) {
        const newInstance = subform.getInstance(instanceId);
        if (newInstance != null) {
            return newInstance;
        }
        subform.Mutator.create(instanceId);
        return subform.getInstance(instanceId);
    }
    addReplaceValue(field, value) {
        const existing = this.validations[field.id] || { fieldId: field.id, fieldName: field.displayName, reason: 'replace', values: [] };
        if (existing.reason !== 'replace') {
            throw new Error('Missing and Replace: ' + field.id);
        }
        if (existing.values.indexOf(value) > -1) {
            return;
        }
        existing.values.push(value);
        this.validations[field.id] = existing;
    }
}
exports.IkeCollectionUpgrade = IkeCollectionUpgrade;
//# sourceMappingURL=collection.upgrade.js.map